import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { TrendingDown, Filter, RotateCcw, Users, FolderOpen, Megaphone, Clock } from "lucide-react";
import { DateRangePicker, openLeadsTab } from "../components/DateRangePicker";
import { appendDateParams } from "../utils/dateFilters";
import SourceBasisToggle from "../components/SourceBasisToggle";
import { last30Days } from "../lib/dateDefaults";

const _DEF = last30Days();

const REASON_COLORS = {
  budget: "#ef4444",
  location: "#f97316",
  competitor: "#8b5cf6",
  project: "#3b82f6",
  personal: "#06b6d4",
  unresponsive: "#6b7280",
  other: "#a3a3a3",
};

const REASON_LABELS = {
  budget: "Budget Mismatch",
  location: "Location Issue",
  competitor: "Bought from Competitor",
  project: "Project Related",
  personal: "Personal Reasons",
  unresponsive: "Unresponsive / Cold",
  other: "Other",
};

const DURATION_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last 12 months" },
];

const Bar = ({ label, count, max, color, onClick }) => {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className={`flex items-center gap-3 ${onClick ? "cursor-pointer hover:bg-slate-50 rounded-lg p-1 -mx-1 transition-colors" : ""}`} data-testid={`bar-${label}`} onClick={onClick}>
      <span className="text-xs text-slate-600 w-32 truncate text-right" title={label}>{label}</span>
      <div className="flex-1 h-7 bg-slate-100 rounded-md overflow-hidden relative">
        <div
          className="h-full rounded-md transition-all duration-500"
          style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color || "#3b82f6" }}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-700">{count}</span>
      </div>
    </div>
  );
};

export default function LostReasonAnalyticsPage() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [options, setOptions] = useState({ projects: [], sources: [], campaigns: [], reps: [] });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(_DEF.from);
  const [dateTo, setDateTo] = useState(_DEF.to);
  const [sourceBasis, setSourceBasis] = useState("latest");

  useEffect(() => {
    axios.get(`${API}/lost-analytics/filter-options`)
      .then(r => setOptions(r.data))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.project) params.set("project", filters.project);
      if (filters.source) params.set("source", filters.source);
      if (filters.campaign) params.set("campaign", filters.campaign);
      if (filters.days) params.set("days", filters.days);
      if (filters.assigned_to) params.set("assigned_to", filters.assigned_to);
      appendDateParams(params, dateFrom, dateTo);
      params.set("source_basis", sourceBasis);
      const res = await axios.get(`${API}/lost-analytics/summary?${params.toString()}`);
      setData(res.data);
    } catch {
      toast.error("Failed to load lost analytics");
    } finally {
      setLoading(false);
    }
  }, [filters, dateFrom, dateTo, sourceBasis]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateFilter = (key, val) => {
    setFilters(prev => {
      const next = { ...prev };
      if (val && val !== "all") next[key] = val;
      else delete next[key];
      return next;
    });
  };

  const clearFilters = () => setFilters({});
  const activeFilterCount = Object.keys(filters).length;

  const reasonMax = data?.by_reason?.reduce((m, r) => Math.max(m, r.count), 0) || 0;
  const subMax = data?.by_sub_reason?.reduce((m, r) => Math.max(m, r.count), 0) || 0;
  const projectMax = data?.by_project?.reduce((m, r) => Math.max(m, r.count), 0) || 0;
  const sourceMax = data?.by_source?.reduce((m, r) => Math.max(m, r.count), 0) || 0;
  const repMax = data?.by_rep?.reduce((m, r) => Math.max(m, r.count), 0) || 0;
  const trendMax = data?.monthly_trend?.reduce((m, t) => Math.max(m, t.count), 0) || 0;

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-[1400px] mx-auto space-y-6" data-testid="lost-analytics-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Lost Reason Analytics</h1>
            <p className="text-sm text-slate-500 mt-1">Understand why leads are being lost and take action</p>
          </div>
          {activeFilterCount > 0 && (
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Clear filters
            </Button>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <SourceBasisToggle value={sourceBasis} onChange={setSourceBasis} />
            <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          </div>
        </div>

        {/* Filters */}
        <Card data-testid="filters-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="text-xs">{activeFilterCount} active</Badge>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Select value={filters.project || "all"} onValueChange={v => updateFilter("project", v)}>
                <SelectTrigger data-testid="filter-project">
                  <FolderOpen className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                  <SelectValue placeholder="Project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {options.projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filters.source || "all"} onValueChange={v => updateFilter("source", v)}>
                <SelectTrigger data-testid="filter-source">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {options.sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filters.campaign || "all"} onValueChange={v => updateFilter("campaign", v)}>
                <SelectTrigger data-testid="filter-campaign">
                  <Megaphone className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                  <SelectValue placeholder="Campaign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {options.campaigns.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filters.days || "all"} onValueChange={v => updateFilter("days", v)}>
                <SelectTrigger data-testid="filter-duration">
                  <Clock className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                  <SelectValue placeholder="Duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  {DURATION_OPTIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={filters.assigned_to || "all"} onValueChange={v => updateFilter("assigned_to", v)}>
                <SelectTrigger data-testid="filter-rep">
                  <Users className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                  <SelectValue placeholder="Sales Rep" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {options.reps.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
          </div>
        ) : !data || data.total_lost === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <TrendingDown className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-lg font-medium text-slate-500">No lost leads found</p>
              <p className="text-sm text-slate-400 mt-1">Adjust filters or check back later</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card data-testid="kpi-total-lost" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ stage: "lost", date_from: dateFrom, date_to: dateTo })}>
                <CardContent className="pt-5 pb-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Total Lost</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{data.total_lost}</p>
                </CardContent>
              </Card>
              <Card data-testid="kpi-loss-rate" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ stage: "lost", date_from: dateFrom, date_to: dateTo })}>
                <CardContent className="pt-5 pb-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Loss Rate</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">{data.loss_rate}%</p>
                </CardContent>
              </Card>
              <Card data-testid="kpi-top-reason" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ stage: "lost", date_from: dateFrom, date_to: dateTo })}>
                <CardContent className="pt-5 pb-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Top Reason</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {REASON_LABELS[data.by_reason?.[0]?.reason] || data.by_reason?.[0]?.reason || "—"}
                  </p>
                  <p className="text-xs text-slate-400">{data.by_reason?.[0]?.count || 0} leads</p>
                </CardContent>
              </Card>
              <Card data-testid="kpi-top-source" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ stage: "lost", source: data.by_source?.[0]?.source, date_from: dateFrom, date_to: dateTo })}>
                <CardContent className="pt-5 pb-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Highest Loss Source</p>
                  <p className="text-lg font-bold text-slate-900 mt-1 capitalize">
                    {data.by_source?.[0]?.source || "—"}
                  </p>
                  <p className="text-xs text-slate-400">{data.by_source?.[0]?.count || 0} leads</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts row 1 - Reason + Sub-reason */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card data-testid="chart-by-reason">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">By Lost Reason</CardTitle>
                  <p className="text-xs text-slate-400 mt-0.5">Primary reason recorded when marking a lead as lost. Click a bar to view those leads.</p>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.by_reason.map(r => (
                    <Bar
                      key={r.reason}
                      label={REASON_LABELS[r.reason] || r.reason}
                      count={r.count}
                      max={reasonMax}
                      color={REASON_COLORS[r.reason] || "#94a3b8"}
                    />
                  ))}
                  {data.by_reason.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data</p>}
                </CardContent>
              </Card>
              <Card data-testid="chart-by-sub-reason">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Top Sub-Reasons</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.by_sub_reason.map(s => (
                    <Bar key={s.sub_reason} label={s.sub_reason} count={s.count} max={subMax} color="#8b5cf6" />
                  ))}
                  {data.by_sub_reason.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No sub-reason data</p>}
                </CardContent>
              </Card>
            </div>

            {/* Monthly trend */}
            <Card data-testid="chart-monthly-trend">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Monthly Trend</CardTitle>
              </CardHeader>
              <CardContent>
                {data.monthly_trend.length > 0 ? (
                  <div className="flex items-end gap-1 h-40">
                    {data.monthly_trend.map(t => {
                      const pct = trendMax > 0 ? (t.count / trendMax) * 100 : 0;
                      return (
                        <div key={t.month} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-[10px] font-medium text-slate-600">{t.count}</span>
                          <div
                            className="w-full bg-red-400 rounded-t-md transition-all duration-500"
                            style={{ height: `${Math.max(pct, 4)}%` }}
                          />
                          <span className="text-[9px] text-slate-400 -rotate-45 origin-top-left whitespace-nowrap">
                            {t.month.slice(5)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No trend data available</p>
                )}
              </CardContent>
            </Card>

            {/* Charts row 2 - Project + Source + Rep */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card data-testid="chart-by-project">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">By Project</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.by_project.map(p => (
                    <Bar key={p.project} label={p.project} count={p.count} max={projectMax} color="#3b82f6" onClick={() => openLeadsTab({ stage: "lost", project: p.project, date_from: dateFrom, date_to: dateTo })} />
                  ))}
                  {data.by_project.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data</p>}
                </CardContent>
              </Card>
              <Card data-testid="chart-by-source">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">By Source</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.by_source.map(s => (
                    <Bar key={s.source} label={s.source} count={s.count} max={sourceMax} color="#f97316" onClick={() => openLeadsTab({ stage: "lost", source: s.source, date_from: dateFrom, date_to: dateTo })} />
                  ))}
                  {data.by_source.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data</p>}
                </CardContent>
              </Card>
              <Card data-testid="chart-by-rep">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">By Sales Rep</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.by_rep.map(r => (
                    <Bar key={r.rep} label={r.rep} count={r.count} max={repMax} color="#06b6d4" onClick={() => openLeadsTab({ stage: "lost", assigned_to: r.rep_id || r.rep, date_from: dateFrom, date_to: dateTo })} />
                  ))}
                  {data.by_rep.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data</p>}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
